import { Box, Grid, TextField, Typography } from "@mui/material";
import useApi from "_api/index";
import CustomizeDialog from "components/Car/CustomizeDialog";
import CustomizeDatePicker from "components/DatePicker";
import { GradientButtonOutline } from "components/GradientButton";
import { useCarBrands } from "contexts/CarBrandsContext";
import { useFormik } from "formik";
import React, { useEffect, useMemo } from "react";

const ModalContractPawnCar = ({
  isOpen,
  onClose,
  data,
  document,
  type = "pawn_car",
  onSuccess,
}) => {
  const { carBrands } = useCarBrands();
  const brandName = useMemo(() => {
    if (data?.car && carBrands.length > 0) {
      const brand = carBrands.find((b) => b.id === data?.car.car_brand_id);
      return brand ? brand.name : "ยี่ห้อรถยนต์";
    }
    return "ยี่ห้อรถยนต์";
  }, [data, carBrands]);

  const formik = useFormik({
    initialValues: {
      date: new Date(),
      debt_security: "",
      interest_rate: "",
      amount: "",
    },
    onSubmit: (values) => {
      // Handle form submission
      console.log("Form submitted with values:", values);
      if (document && document.document_id) {
        handleUpdatePawnContract(values);
      } else {
        handleCreatePawnContract(values);
      }
    },
  });

  useEffect(() => {
    if (isOpen && data) {
      if (document && document.document_id) {
        fetchDataDocuments();
      } else {
        const valuesFormatted = {
          ...formik.values,
          date: new Date(),
          debt_security: "",
          interest_rate: data?.interest_rate || "",
          amount: data?.car?.car_price || "",
        };
        formik.setValues(valuesFormatted);
      }
    }
  }, [isOpen, data]);

  const fetchDataDocuments = async () => {
    try {
      const response = await useApi.pawn_car.getDetailCarPawnContactById(
        document.document_id
      );
      if (response.success) {
        formik.setValues({
          ...formik.values,
          date: response.data.date || new Date(),
          debt_security: response.data.debt_security || "",
          interest_rate: response.data.interest_rate || "",
          amount: response.data.amount || "",
        });
      }
    } catch (error) {}
  };

  const handleCreatePawnContract = async (values) => {
    try {
      const payload = {
        ...values,
      };
      const response = await useApi.pawn_car.createCarPawnContact(
        payload,
        data.order_id
      );
      if (response.success) {
        // Handle success (e.g., show a success message, close modal, etc.)
        onClose();
        onSuccess && onSuccess();
      } else {
        // Handle error (e.g., show an error message)
        console.error("Error creating contract:", response.message);
      }
    } catch (error) {
      console.error("Error creating contract:", error);
    }
  };

  const handleUpdatePawnContract = async (values) => {
    try {
      const payload = {
        ...values,
      };
      const response = await useApi.pawn_car.updateCarPawnContact(
        payload,
        document.document_id
      );
      if (response.success) {
        // Handle success (e.g., show a success message, close modal, etc.)
        onClose();
        onSuccess && onSuccess();
      } else {
        // Handle error (e.g., show an error message)
        console.error("Error updating contract:", response.message);
      }
    } catch (error) {
      console.error("Error updating contract:", error);
    }
  };
  return (
    <CustomizeDialog
      open={isOpen}
      onClose={onClose}
      title={document ? "แก้ไขสัญญาจำนำ" : "สร้างสัญญาจำนำ"}
      maxWidth="md"
      fullWidth
      size="md"
      onSave={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            gap: 1,
            width: "100%",
            "& > button": {
              height: "40px",
              cursor: "default",
              pointerEvents: "none",
              background: "rgba(0, 187, 159, 0.2)",
              "&:hover": {
                background: "transparent",
              },
              boxShadow: "none",
            },
          }}
        >
          <GradientButtonOutline>
            {data?.car ? data?.car.car_type : "รถยนต์"}
          </GradientButtonOutline>
          <GradientButtonOutline>
            {data?.car ? brandName : "ยี่ห้อรถยนต์"}
          </GradientButtonOutline>
          <GradientButtonOutline>
            {data?.car
              ? data?.car.license_plate + " " + data?.car.license_province
              : "ทะเบียนรถ"}
          </GradientButtonOutline>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            สัญญาจำนำ
          </Typography>
          <Grid container spacing={2} px={1}>
            <Grid item xs={12}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={2}>
                  <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                    วันที่
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <CustomizeDatePicker
                    value={formik.values["date"]}
                    onChange={(newValue) => {
                      formik.setFieldValue("date", newValue);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "white",
                        height: "40px",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Typography variant="h5" sx={{ mb: 1 }}>
            ข้อมูลจำนำ
          </Typography>
          <Grid container spacing={2} px={1}>
            <Grid item xs={12}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={2}>
                  <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                    การประกันหนี้
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <TextField
                    fullWidth
                    type="text"
                    variant="outlined"
                    name="debt_security"
                    placeholder="การประกันหนี้"
                    value={formik.values["debt_security"] || ""}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      formik.setFieldValue("debt_security", e.target.value);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "white",
                        height: "40px",
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={2}>
                  <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                    จำนวนเงิน
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    type="number"
                    variant="outlined"
                    name="amount"
                    placeholder="จำนวนเงิน"
                    value={formik.values["amount"] || ""}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      formik.setFieldValue("amount", e.target.value);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "white",
                        height: "40px",
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                    บาท
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={2}>
                  <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                    อัตราดอกเบี้ย
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    type="number"
                    variant="outlined"
                    name="interest_rate"
                    placeholder="อัตราดอกเบี้ย"
                    value={formik.values["interest_rate"] || ""}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      formik.setFieldValue("interest_rate", e.target.value);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "white",
                        height: "40px",
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                    เปอร์เซ็นต์/ปี
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </form>
    </CustomizeDialog>
  );
};

export default ModalContractPawnCar;
